import { supplierHubEmailLimits } from '@Constants/supplierHub.constants';

export const validateFileUpload = (file: File): { isValid: boolean; error?: string } => {
    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    const allowedExtensions = ['.jpg', '.jpeg', '.png', '.webp'];

    // Check file size
    if (file.size > maxSize) {
        return {
            isValid: false,
            error: 'File size must be less than 5MB'
        };
    }

    // Check file type
    if (!allowedTypes.includes(file.type)) {
        return {
            isValid: false,
            error: 'Only JPEG, PNG, and WebP files are allowed'
        };
    }

    // Check file extension
    const extension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    if (!allowedExtensions.includes(extension)) {
        return {
            isValid: false,
            error: 'Invalid file extension'
        };
    }

    // Additional security checks
    if (file.name.includes('../') || file.name.includes('..\\')) {
        return {
            isValid: false,
            error: 'Invalid file name'
        };
    }

    return { isValid: true };
};

export const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email) && email.length <= 254;
};

export const validatePhone = (phone: string): boolean => {
    const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
    return phoneRegex.test(phone.replace(/[\s\-\(\)]/g, ''));
};

/**
 * Validates supplier email subject and body
 * @param t Translation function
 * @param emailSubject Email subject
 * @param emailBody Email body
 * @param subjectLimit Maximum number of characters for subject
 * @param bodyLimit Maximum number of characters for body
 * @returns Object with validation errors (if any)
 */
export const validateSupplierEmail = (
    t: (key: string, params?: any) => string,
    emailSubject: string,
    emailBody: string,
    subjectLimit: number = supplierHubEmailLimits.subject,
    bodyLimit: number  = supplierHubEmailLimits.body,
): {subject?: string, body?: string} => {
    const errors: {subject?: string, body?: string} = {};

    if (!emailSubject.trim()) {
        errors.subject = t('validation.fieldIRequired');
    } else if (emailSubject.length > subjectLimit) {
        errors.subject = t('validation.longText');
    }

    if (!emailBody.trim()) {
        errors.body = t('validation.fieldIRequired');
    } else if (emailBody.length > bodyLimit) {
        errors.body = t('validation.longText');
    }

    return errors;
};

/**
 * Validates numeric range fields for the search niche form
 * @param min Minimum value
 * @param max Maximum value
 * @returns Validation result with error message if invalid
 */
export const validateNumericRange = (min?: number, max?: number): { isValid: boolean; error?: string } => {
  // If both values are undefined, it's valid (no filter applied)
  if (min === undefined && max === undefined) {
    return { isValid: true };
  }

  // Check for negative values
  if (min !== undefined && min < 0) {
    return {
      isValid: false,
      error: 'Value cannot be negative'
    };
  }

  if (max !== undefined && max < 0) {
    return {
      isValid: false,
      error: 'Value cannot be negative'
    };
  }

  // If only one value is provided, it's valid
  if ((min !== undefined && max === undefined) || (min === undefined && max !== undefined)) {
    return { isValid: true };
  }

  // Both values are provided, check if min is less than or equal to max
  if (min !== undefined && max !== undefined && min > max) {
    return {
      isValid: false,
      error: 'Minimum value must be less than or equal to maximum value'
    };
  }

  // Values are valid
  return { isValid: true };
};

/**
 * Validates keyword field for search niche
 * @param keyword Search keyword
 * @returns Validation result with error message if invalid
 */
// ASIN: 10-char alphanumeric starting with B0
const ASIN_REGEX = /\b[B0-9][A-Z0-9]{9}\b/g;

export const detectAsins = (text: string): string[] => {
    const matches = text.toUpperCase().match(ASIN_REGEX);
    if (!matches) return [];
    return [...new Set(matches)];
};

export const isAsin = (text: string): boolean => {
    const trimmed = text.trim().toUpperCase();
    ASIN_REGEX.lastIndex = 0;
    return ASIN_REGEX.test(trimmed) && trimmed.length === 10;
};

export const validateSearchKeyword = (keyword: string): { isValid: boolean; error?: string } => {
  if (!keyword || keyword.trim() === '') {
    return {
      isValid: false,
      error: 'Keyword is required'
    };
  }

  // Check for minimum length
  if (keyword.trim().length < 2) {
    return {
      isValid: false,
      error: 'Keyword must be at least 2 characters'
    };
  }

  // Check for maximum length
  if (keyword.trim().length > 100) {
    return {
      isValid: false,
      error: 'Keyword must be less than 100 characters'
    };
  }

  return { isValid: true };
};

/**
 * Validates review rating range
 * @param min Minimum rating (1-5)
 * @param max Maximum rating (1-5)
 * @returns Validation result with error message if invalid
 */
export const validateRatingRange = (min?: number, max?: number): { isValid: boolean; error?: string } => {
  // Basic range validation
  const rangeValidation = validateNumericRange(min, max);
  if (!rangeValidation.isValid) {
    return rangeValidation;
  }

  // Check if values are within 1-5 range
  if (min !== undefined && (min < 1 || min > 5)) {
    return {
      isValid: false,
      error: 'Rating must be between 1 and 5'
    };
  }

  if (max !== undefined && (max < 1 || max > 5)) {
    return {
      isValid: false,
      error: 'Rating must be between 1 and 5'
    };
  }

  return { isValid: true };
};

/**
 * Validates date range for history filters
 * @param from Start date
 * @param to End date
 * @returns Validation result with error message if invalid
 */
export const validateDateRange = (from?: string, to?: string): { isValid: boolean; error?: string } => {
  // If both values are undefined, it's valid (no filter applied)
  if (!from && !to) {
    return { isValid: true };
  }

  // If only one value is provided, it's valid
  if ((from && !to) || (!from && to)) {
    return { isValid: true };
  }

  // Both values are provided, check if from is less than or equal to to
  if (from && to) {
    const fromDate = new Date(from);
    const toDate = new Date(to);

    // Check if dates are valid
    if (isNaN(fromDate.getTime()) || isNaN(toDate.getTime())) {
      return {
        isValid: false,
        error: 'Invalid date format'
      };
    }

    if (fromDate > toDate) {
      return {
        isValid: false,
        error: 'Start date must be before or equal to end date'
      };
    }
  }

  return { isValid: true };
};
